<template>
  <div class="vlist-root">
    <!-- Filters bar -->
    <section class="vlist-filters">
      <div class="search-wrap">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by username, name, email, phone…"
          class="search-input"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="search-clear"
          @click="searchQuery = ''"
          title="Clear search"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="chip-group">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          type="button"
          class="chip"
          :class="{ 'chip-active': statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >{{ opt.label }}</button>
      </div>
    </section>

    <!-- Body -->
    <div v-if="isLoading" class="vlist-loading">
      <div class="loading-spinner"></div>
      <div class="loading-label">Loading vendors…</div>
    </div>

    <div v-else-if="!hasFilteredUsers" class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke-linecap="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No vendors found</div>
      <div class="empty-hero-sub">
        {{ searchQuery ? 'Try clearing your search or filters.' : 'Create your first vendor to get started.' }}
      </div>
      <button v-if="!searchQuery" class="btn btn-primary btn-inline" @click="$emit('create-clicked')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
          <path d="M12 5v14M5 12h14" stroke-linecap="round" />
        </svg>
        New Vendor
      </button>
    </div>

    <div v-else class="vlist-body">
      <div class="vlist-grid">
        <article
          v-for="(vendor, index) in pagedUsers"
          :key="`${vendor.username}-${index}`"
          class="vendor-card"
          @click="editUser(vendor)"
        >
          <div class="card-top">
            <div class="avatar">{{ initialsOf(vendor) }}</div>
            <div class="vendor-meta">
              <div class="vendor-name">{{ vendor.name || vendor.username }}</div>
              <div class="vendor-sub">
                <span>@{{ vendor.username }}</span>
                <span v-if="vendor.email" class="dot-sep">·</span>
                <span v-if="vendor.email" class="vendor-email">{{ vendor.email }}</span>
              </div>
            </div>
            <div class="card-badges">
              <span
                v-if="Number(vendor.can_manage_clients) === 1"
                class="pill pill-info"
                title="This vendor can manage their own clients + view Admin Report."
              >Self-serve</span>
              <span
                class="pill"
                :class="vendor.status ? 'pill-success' : 'pill-neutral'"
              >{{ vendor.status ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>

          <div v-if="hasStats(vendor)" class="card-stats">
            <div class="stat-cell">
              <div class="stat-label">Pay-in today</div>
              <div class="stat-value">{{ formatMoney(getStat(vendor, 'payInApprovedAmount')) }}</div>
              <div class="stat-sub">{{ getStat(vendor, 'payInApprovedCount') || 0 }} orders</div>
            </div>
            <div class="stat-cell">
              <div class="stat-label">Pay-out today</div>
              <div class="stat-value">{{ formatMoney(getStat(vendor, 'payOutApprovedAmount')) }}</div>
              <div class="stat-sub">{{ getStat(vendor, 'payOutApprovedCount') || 0 }} orders</div>
            </div>
            <div class="stat-cell">
              <div class="stat-label">Contact</div>
              <div class="stat-value stat-mono">{{ vendor.phone || '—' }}</div>
              <div class="stat-sub">phone</div>
            </div>
          </div>
          <div v-else-if="isLoadingStats" class="card-stats card-stats-loading">
            <div class="skeleton"></div>
            <div class="skeleton"></div>
            <div class="skeleton"></div>
          </div>

          <div class="card-actions" @click.stop>
            <button
              class="action-btn action-report"
              @click.stop="openVendorReport(vendor)"
              title="View vendor report"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3v18h18" stroke-linecap="round" />
                <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Report
            </button>
            <div class="action-toggle">
              <span class="toggle-label">Status</span>
              <base-toggle
                :key="`toggle-${vendor.username}-${toggleVersions[vendor.username] || 0}`"
                class="mb-0"
                :is-checked="vendor.status === 1"
                @rsd-checkbox-changed="toggleStatus(vendor)"
              ></base-toggle>
            </div>
            <button
              class="action-btn action-edit"
              @click.stop="editUser(vendor)"
              title="Edit vendor"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9" stroke-linecap="round" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              Edit
            </button>
          </div>
        </article>
      </div>

      <div class="pagination-wrap">
        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
      </div>
    </div>

    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="openVendorStatistics" class="modal-backdrop" @click.self="closeVendorReport">
          <div class="modal-card modal-card-wide">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">Vendor report</div>
                <div class="modal-title">{{ selectedVendorUsername }}</div>
                <div class="modal-sub">Pay-in and pay-out activity across all statuses.</div>
              </div>
              <button type="button" class="modal-close" @click="closeVendorReport">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                  <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
                </svg>
              </button>
            </div>
            <div class="modal-body modal-body-scroll">
              <vendor-report :vendor="selectedVendorUsername"></vendor-report>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import VendorReport from "../../components/vendor/VendorReport.vue";
import Pagination from "../../components/shared/pagination.vue";
import http from "../../http-common";
import moment from "moment-timezone";

export default {
  name: "vendor-list",
  emits: ["edit-user", "create-clicked", "toast", "error"],
  components: { VendorReport, Pagination },
  data() {
    return {
      isLoading: false,
      isLoadingStats: false,
      statsDate: moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD"),
      vendorStats: {},
      page: 1,
      perPage: 12,
      openVendorStatistics: false,
      selectedVendorUsername: "",
      searchQuery: "",
      statusFilter: "active",
      statusOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
      toggleVersions: {},
    };
  },
  created() {
    this.getUsers();
  },
  watch: {
    searchQuery() { this.page = 1; },
    statusFilter() { this.page = 1; },
  },
  computed: {
    allUsers() {
      return this.$store.getters["vendor/users"];
    },
    filteredUsers() {
      let filtered = this.allUsers || [];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        const matches = (v) => v != null && String(v).toLowerCase().includes(q);
        filtered = filtered.filter((u) =>
          matches(u.username) || matches(u.name) || matches(u.email) || matches(u.phone)
        );
      }
      if (this.statusFilter === "active") {
        filtered = filtered.filter((u) => u.status === 1);
      } else if (this.statusFilter === "inactive") {
        filtered = filtered.filter((u) => u.status === 0);
      }
      // Active first, then inactive.
      return [...filtered].sort((a, b) => (a.status === 1 ? 0 : 1) - (b.status === 1 ? 0 : 1));
    },
    hasFilteredUsers() {
      return this.filteredUsers.length > 0;
    },
    pagedUsers() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredUsers.slice(start, start + this.perPage);
    },
    paginatorInfo() {
      const total = this.filteredUsers.length;
      const lastPage = Math.ceil(total / this.perPage) || 1;
      const firstItem = total === 0 ? 0 : (this.page - 1) * this.perPage + 1;
      const lastItem = Math.min(this.page * this.perPage, total);
      return {
        total,
        currentPage: this.page,
        lastPage,
        firstItem,
        lastItem,
        hasMorePages: this.page < lastPage,
        perPage: this.perPage,
      };
    },
  },
  methods: {
    setPage(page) { this.page = page; },
    initialsOf(v) {
      const s = (v.name || v.username || "?").trim();
      const parts = s.split(/\s+/);
      const a = parts[0]?.[0] || "?";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase().slice(0, 2);
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    hasStats(vendor) {
      const s = this.vendorStats[vendor.username];
      if (!s) return false;
      return (
        Number(s.payInApprovedCount) > 0 ||
        Number(s.payOutApprovedCount) > 0 ||
        !!vendor.phone
      );
    },
    getStat(vendor, key) {
      const s = this.vendorStats[vendor.username];
      return s ? s[key] : 0;
    },
    async getUsers() {
      this.isLoading = true;
      try {
        await this.$store.dispatch("vendor/getUsers");
        await this.loadStats();
      } catch (error) {
        // Bubble up so parent can flip the permission gate for 401/403 etc.
        this.$emit("error", error);
      }
      this.isLoading = false;
    },
    async loadStats() {
      this.isLoadingStats = true;
      try {
        const response = await http.get(`/dashboard/list-stats?type=vendor&date=${this.statsDate}`);
        if (response.data.success) {
          const map = {};
          response.data.data.forEach((item) => { map[item.username] = item; });
          this.vendorStats = map;
        }
      } catch (e) {
        console.error("Error loading vendor stats:", e);
      }
      this.isLoadingStats = false;
    },
    editUser(vendor) { this.$emit("edit-user", vendor); },
    openVendorReport(vendor) {
      this.selectedVendorUsername = vendor.username;
      this.openVendorStatistics = true;
    },
    closeVendorReport() {
      this.openVendorStatistics = false;
      this.selectedVendorUsername = "";
    },
    async toggleStatus(vendor) {
      const newStatus = vendor.status === 1 ? 0 : 1;
      const action = newStatus === 0 ? "deactivate" : "activate";
      const title = newStatus === 0
        ? `Deactivate vendor "${vendor.username}"?`
        : `Activate vendor "${vendor.username}"?`;
      const html = newStatus === 0
        ? `<div class="text-left"><p class="mb-2"><strong>This is a high-impact action.</strong></p><p>All <strong>new orders</strong> for this vendor (and every client under it) will be <strong>stopped immediately</strong>.</p><p class="mt-2 text-sm text-gray-600">Existing orders are not changed.</p></div>`
        : `<div class="text-left"><p>This vendor and its clients will be allowed to create new orders again.</p></div>`;
      this.$swal.fire({
        title,
        html,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: newStatus === 0 ? "#d33" : "#3085d6",
        cancelButtonColor: "#6b7280",
        confirmButtonText: `Yes, ${action} vendor`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.$store.dispatch("vendor/changeUserStatus", {
              username: vendor.username,
              status: newStatus,
            });
            this.$emit("toast", { type: "success", message: `Vendor ${action}d successfully!` });
          } catch (error) {
            this.$emit("error", error);
            this.bumpToggleVersion(vendor.username);
          }
        } else {
          this.bumpToggleVersion(vendor.username);
        }
      });
    },
    bumpToggleVersion(username) {
      this.toggleVersions = {
        ...this.toggleVersions,
        [username]: (this.toggleVersions[username] || 0) + 1,
      };
    },
  },
};
</script>

<style scoped>
.vlist-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

/* Filters bar */
.vlist-filters {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

.chip-group { display: flex; gap: 4px; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
.chip:hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* Body */
.vlist-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.vlist-loading {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label { font-size: 13px; color: #64748b; }

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.empty-hero-icon {
  width: 60px; height: 60px;
  border-radius: 16px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 30px; height: 30px; }
.empty-hero-title { font-size: 16px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 13px; color: #64748b; }
.btn-inline { margin-top: 8px; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  outline: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }

/* Card grid */
.vlist-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 1200px) {
  .vlist-grid { grid-template-columns: 1fr 1fr; }
}

.vendor-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.vendor-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 22px -10px rgba(27, 42, 71, 0.22);
  transform: translateY(-1px);
}

.card-top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.avatar {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1B2A47, #2C3E63);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.vendor-meta { min-width: 0; }
.vendor-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vendor-sub {
  font-size: 12px;
  color: #64748b;
  display: flex;
  gap: 6px;
  align-items: center;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.dot-sep { color: #cbd5e1; }
.vendor-email { color: #475569; font-weight: 500; }

.card-badges {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.pill-success { background: #d1fae5; color: #065f46; }
.pill-neutral { background: #f1f5f9; color: #64748b; }
.pill-info    { background: #eef2f7; color: #1B2A47; }

/* Stats */
.card-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}
.card-stats-loading .skeleton {
  height: 46px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 400% 100%;
  border-radius: 8px;
  animation: shine 1.4s ease-in-out infinite;
}
@keyframes shine {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.stat-cell {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 8px 10px;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}
.stat-mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 13px; }
.stat-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
@media (max-width: 720px) {
  .card-stats { grid-template-columns: 1fr 1fr; }
  .card-stats > .stat-cell:nth-child(3) { grid-column: 1 / -1; }
}

/* Card actions */
.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 14px; height: 14px; }
.action-btn:hover { border-color: #cbd5e1; color: #1B2A47; background: #eef2f7; }
.action-report:hover { color: #059669; background: #ecfdf5; border-color: #a7f3d0; }
.action-edit:hover  { color: #1B2A47; background: #eef2f7; border-color: #cbd5e1; }

.action-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}
.toggle-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.pagination-wrap {
  margin-top: auto;
  padding-top: 8px;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ==================== Report modal (teleported) ==================== */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}
.modal-card-wide { max-width: 840px; }
.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.modal-sub { font-size: 12.5px; color: #64748b; }
.modal-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.modal-close:hover { background: #f1f5f9; color: #1b2a47; }
.modal-close svg { width: 16px; height: 16px; }

.modal-body {
  padding: 20px 22px;
}
.modal-body-scroll { overflow-y: auto; max-height: calc(100vh - 200px); }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.22s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from .modal-card { transform: translateY(-8px) scale(0.98); }
</style>
